/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { Info, Search } from "lucide-react";
import type { ApiResult } from "../api/client";
import type { Paged } from "../api/registry";
import { ErrorMessage } from "./ui/ErrorMessage";
import { InfiniteList, type InfiniteListProps } from "./ui/InfiniteList";
import { Input } from "./ui/Input";
import { Spinner } from "./ui/Spinner";
import { Tooltip } from "./ui/Tooltip";
import { EmptyState } from "./ui/EmptyState";
import { debounce } from "../lib/utils";
import { useVirtualRows } from "./ui/useVirtualRows";
import type { ListView } from "./ViewToggle";

const DEBOUNCE_MILLIS = 300;

/**
 * The one registry directory shell: debounced search over a paged nexus list, rendered as cards
 * or, when the page offers one and the viewer picks it, as a table. Pages differ only in their
 * card, their table and their filters, so those are props.
 */
export type EmptyCopy = { icon: ReactNode; title: string; description: string };

/** What a page's table needs from the list: the rows and the same paging the cards get. */
export type TableList<T> = Omit<InfiniteListProps, "children"> & { items: T[] };

export function RegistryDirectory<T>({
  placeholder,
  countNoun,
  countNounPlural = `${countNoun}s`,
  countHint,
  load,
  itemKey,
  renderCard,
  empty,
  controls,
  filters,
  filterKey = "",
  view = "cards",
  table,
}: {
  placeholder: string;
  countNoun: string;
  countNounPlural?: string;
  countHint?: string;
  /** Fixed-width controls that stay beside the search box, such as a sort select. */
  controls?: ReactNode;
  /** The filter bar; its active-filter chips take the row below the search. */
  filters?: ReactNode;
  filterKey?: string;
  load: (q: string, page: number) => Promise<ApiResult<Paged<T>>>;
  itemKey: (item: T) => number | string;
  renderCard: (item: T, index: number) => ReactNode;
  empty: EmptyCopy;
  view?: ListView;
  /** The table view; shown instead of the cards when the view is "table". */
  table?: (list: TableList<T>) => ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const setQ = (value: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === "") {
          next.delete("q");
        } else {
          next.set("q", value);
        }
        return next;
      },
      { replace: true },
    );
  };
  const [items, setItems] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useMemo(
    () => debounce((value: string) => setQ(value), DEBOUNCE_MILLIS),
    [],
  );
  useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery]);

  const requestSeq = useRef(0);
  const loadPage = (pageToLoad: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    void load(q, pageToLoad).then((result) => {
      if (seq !== requestSeq.current) {
        return;
      }
      setLoading(false);
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      setError(null);
      setHasMore(result.value.hasMore);
      setTotal(result.value.total);
      setPage(pageToLoad);
      if (pageToLoad === 0) {
        setItems(result.value.items);
        return;
      }
      setItems((previous) => {
        const seen = new Set((previous ?? []).map(itemKey));
        return [
          ...(previous ?? []),
          ...result.value.items.filter((item) => !seen.has(itemKey(item))),
        ];
      });
    });
  };
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    setItems(null);
    loadPageRef.current(0);
  }, [q, filterKey]);

  const loadNext = () => loadPageRef.current(page + 1);
  const list = {
    items: items ?? [],
    isLoading: loading,
    hasMore,
    onLoadMore: loadNext,
    isEmpty: items !== null && items.length === 0,
    currentPage: page,
    error,
    onRetry: loadNext,
  };
  const emptyState = (
    <EmptyState
      icon={empty.icon}
      tone={q === "" ? "quiet" : "search"}
      title={empty.title}
      description={empty.description}
      actionLabel={q === "" ? undefined : "Clear search"}
      onAction={
        q === ""
          ? undefined
          : () => {
              setDraft("");
              setQ("");
            }
      }
    />
  );

  return (
    <div className="space-y-6">
      <div
        className="enter flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
        style={{ "--enter-delay": "80ms" } as CSSProperties}
      >
        <div className="min-w-0 w-full flex-1 sm:max-w-xl">
          <Input
            aria-label={placeholder}
            placeholder={placeholder}
            prefix={<Search size={15} className="text-text-tertiary" />}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              debouncedQuery(event.target.value);
            }}
          />
        </div>
        {controls}
        {filters}
      </div>

      {items === null && error !== null ? (
        <ErrorMessage message={error} />
      ) : items === null ? (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          <p
            className="flex items-center gap-1.5 text-sm text-text-tertiary"
            aria-live="polite"
          >
            <span>
              {total === 1
                ? `1 ${countNoun}`
                : `${total.toLocaleString("en-US")} ${countNounPlural}`}
            </span>
            {countHint && (
              <Tooltip
                content={countHint}
                className="max-w-xs whitespace-normal text-left"
              >
                <Info
                  size={14}
                  aria-label="About this count"
                  className="text-text-tertiary"
                />
              </Tooltip>
            )}
            {loading && (
              <Spinner size="xs" className="inline-block align-middle" />
            )}
          </p>
          {view === "table" && table ? (
            table({ ...list, emptyState })
          ) : (
            <InfiniteList {...list} emptyState={emptyState}>
              <CardGrid items={items} renderCard={renderCard} />
            </InfiniteList>
          )}
        </div>
      )}
    </div>
  );
}

const CARD_GAP = 16;

/**
 * The card grid with only the rows of cards near the viewport in the DOM. Cards on one page share
 * a height, so the first card measures the row and the grid's own template says how many sit in
 * it; the space of the rest is kept as padding so scrolling and the loader below stay honest.
 */
function CardGrid<T>({
  items,
  renderCard,
}: {
  items: T[];
  renderCard: (item: T, index: number) => ReactNode;
}) {
  const listRef = useRef<HTMLOListElement>(null);
  const [shape, setShape] = useState({ columns: 1, rowHeight: 0 });

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const measure = () => {
      const columns = getComputedStyle(list).gridTemplateColumns.split(" ").filter(Boolean).length;
      const card = list.firstElementChild;
      const rowHeight = card ? card.getBoundingClientRect().height + CARD_GAP : 0;
      setShape((previous) =>
        previous.columns === columns && previous.rowHeight === rowHeight
          ? previous
          : { columns: Math.max(1, columns), rowHeight },
      );
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(list);
    return () => observer?.disconnect();
  }, [items.length]);

  const rowWindow = useVirtualRows({
    count: Math.ceil(items.length / shape.columns),
    rowHeight: shape.rowHeight,
    anchor: listRef,
  });
  const first = rowWindow.start * shape.columns;
  const last = rowWindow.end * shape.columns;

  return (
    <ol
      ref={listRef}
      className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      style={{
        paddingTop: rowWindow.padTop,
        paddingBottom: Math.max(0, rowWindow.padBottom - CARD_GAP),
      }}
    >
      {items.slice(first, last).map((item, index) => renderCard(item, first + index))}
    </ol>
  );
}
